import { DependencyInfoOptions } from '../../../../../../shared/reports';
import { ReportOptionDetails } from '../utils';
import type { AttributionReportIncludeOptions as ReportOptions } from '../../../../../../shared/reports';
import type { FixPlanReportOptions, ReportCategory, ReportFormatType } from '../../shared/types';

import type { DependencyMetadataFilterOption, FormatName, ReportType } from './types';

/**
 * The title of the report page for each report type.
 */
export const previewTitleMap = {
  licensing: 'Licensing',
  sbom: 'SBOM',
  'remediation-guidance': 'Remediation Guidance',
  vulnerability: 'Vulnerabilities',
  settings: 'Settings',
};

type OptionalFormatNames = {
  [K in FormatName | 'default']?: string;
};

/**
 * Use to display the format in the preview header.
 */
export const previewFormatMap: Record<ReportType, OptionalFormatNames> = {
  licensing: {
    PDF: 'PDF',
    HTML: 'Preview',
    CSV: 'CSV',
    Markdown: 'Markdown',
    'Plain Text': 'Plain Text',
    default: 'Preview',
  },
  sbom: {
    default: 'SPDX (tag/value) Preview',
    SPDX: 'SPDX (tag/value) Preview',
    'CycloneDX (JSON)': 'CycloneDX (JSON) Preview',
    'CycloneDX (XML)': 'CycloneDX (XML) Preview',
    'SPDX (JSON)': 'SPDX (JSON) Preview',
  },
  vulnerability: { default: 'PDF' },
  'remediation-guidance': {
    default: 'Preview PDF Report',
    PDF: 'Preview PDF Report',
    HTML: 'Preview HTML Report',
    JSON: 'Preview JSON Report',
  },
};

/**
 * The backend formats for each frontend format.
 */
export const reportTypeFormatMap: Record<FormatName, ReportFormatType> = {
  'CycloneDX (JSON)': 'CYCLONEDX_JSON',
  'CycloneDX (XML)': 'CYCLONEDX_XML',
  SPDX: 'SPDX',
  'SPDX (JSON)': 'SPDX_JSON',
  CSV: 'CSV',
  HTML: 'HTML',
  Markdown: 'MD',
  PDF: 'PDF',
  'Plain Text': 'TXT',
  JSON: 'JSON',
};

/**
 * The default backend format for each report type.
 */
export const reportTypeDefaultFormatMap: Record<ReportCategory, ReportFormatType> = {
  licensing: reportTypeFormatMap.HTML,
  sbom: reportTypeFormatMap.SPDX,
  vulnerability: reportTypeFormatMap.PDF,
  'remediation-guidance': reportTypeFormatMap.PDF,
};

/**
 * The default options for the include checkboxes.
 */
export const defaultIncludeOptions: Array<keyof ReportOptions> = [
  'includeDirectDependencies',
  'includeDeepDependencies',
  'includeFOSSADependencies',
  'includeLicenseList',
];

/**
 * The default options for the include license checkboxes. (These options should be nested under the "List of licenses" section.)
 */
export const licenseIncludeOptions = new Set<keyof ReportOptions>([
  'includeFileMatches',
  'includeProjectLicense',
  'includeLicenseScan',
  'includeLicenseList',
  'includeCopyrightList',
  'includeNonLicenseCopyrights',
  'includeConcludedLicenses',
]);

/**
 * The include checkbox options when NTIA/FDA toggle is selected.
 */
export const ntiaflatIncludeOptions = [
  {
    label: ReportOptionDetails.includeDirectDependencies.title,
    value: ReportOptionDetails.includeDirectDependencies.key,
  },
  {
    label: ReportOptionDetails.includeDeepDependencies.title,
    value: ReportOptionDetails.includeDeepDependencies.key,
  },
];

/**
 * The include checkbox values when NTIA/FDA toggle is selected.
 */
export const ntiaflatIncludeValues = new Set(ntiaflatIncludeOptions.map(({ value }) => value));

/**
 * The license checkbox options when NTIA/FDA toggle is selected.
 */
export const ntiaLicenseOptions = [
  {
    label: ReportOptionDetails.includeLicenseScan.title,
    value: ReportOptionDetails.includeLicenseScan.key,
  },
  {
    label: ReportOptionDetails.includeLicenseList.title,
    value: ReportOptionDetails.includeLicenseList.key,
  },
  {
    label: ReportOptionDetails.includeFileMatches.title,
    value: ReportOptionDetails.includeFileMatches.key,
  },
  {
    label: ReportOptionDetails.includeCopyrightList.title,
    value: ReportOptionDetails.includeCopyrightList.key,
  },
];

/**
 * The license checkbox values when NTIA/FDA toggle is selected.
 */
export const ntiaLicenseValues = new Set(ntiaLicenseOptions.map(({ value }) => value));

/**
 * When we unset the NTIA/FDA toggle, we want to uncheck everything in the ntiaLicenseValues list
 * but also the includeNonLicenseCopyrights checkbox, as we are unchecking the copyrights checkbox, which will
 * disable the includeNonLicenseCopyrights checkbox
 */
export const ntiaLicenseUncheckValues = ntiaLicenseValues.add(ReportOptionDetails.includeNonLicenseCopyrights.key);

/**
 * The metadata checkbox options when NTIA/FDA toggle is selected.
 */
export const ntiaMetadataCheckboxOptions = [
  {
    label: DependencyInfoOptions.License,
    value: 'License',
  },
  {
    label: DependencyInfoOptions.OtherLicenses,
    value: 'OtherLicenses',
  },
  {
    label: DependencyInfoOptions.Authors,
    value: 'Authors',
  },
  {
    label: DependencyInfoOptions.PackageDownloadUrl,
    value: 'PackageDownloadUrl',
  },
];

/**
 * The metadata checkbox values when NTIA/FDA toggle is selected.
 */
export const ntiaMetadataCheckboxValues = new Set<DependencyMetadataFilterOption>(
  ntiaMetadataCheckboxOptions.map(({ value }) => value as DependencyMetadataFilterOption),
);

/**
 * The default options for the fix plan report - buildFixPlanParams currently assumes this includes all options
 */
export const defaultFixPlanOptions: Array<keyof FixPlanReportOptions> = [
  'includeLowPriority',
  'includeHighPriority',
  'includeQuickWins',
  'includeOutdatedDependencies',
];
